/**
 * MentalityComposer — single rounded composer modeled on ChatRoom's
 * input-container: chips row (attached doc status) → textarea → actions.
 *
 * Owns the textarea auto-grow and the file picker handle. Actual doc
 * state lives in useMentalityDocument; this component is presentational.
 */

import { useRef } from 'react';
import {
    ArrowUp, RefreshCw, AlertTriangle, Paperclip, X,
} from 'lucide-react';
import { ButtonSpinner } from '../../../core/ui/ButtonSpinner';
import { GLOSSARY } from '../glossary';

type Props = {
    query: string;
    onQueryChange: (next: string) => void;
    onSend: () => void;
    sending: boolean;
    hasAnyContent: boolean;
    docName: string;
    docText: string;
    uploading: boolean;
    uploadTruncated: boolean;
    uploadError: string | null;
    onUpload: (file: File) => void;
    onClearDoc: () => void;
    onDismissError: () => void;
};

export default function MentalityComposer({
    query, onQueryChange, onSend, sending, hasAnyContent,
    docName, docText, uploading, uploadTruncated, uploadError,
    onUpload, onClearDoc, onDismissError,
}: Props) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (e.target) e.target.value = '';
        if (file) onUpload(file);
    };

    const hasChip = docName || docText || uploading || uploadError;

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.docx,.txt,.md,.rst"
                onChange={handleFileChange}
                className="mentality-overlay__file-input"
                aria-hidden="true"
            />

            <div className="mentality-overlay__composer">
                {hasChip && (
                    <div className="mentality-overlay__chips">
                        {uploading ? (
                            <div className="mentality-overlay__chip is-loading" role="status" aria-live="polite">
                                <ButtonSpinner size="sm" />
                                <span>Uploading attachment</span>
                            </div>
                        ) : uploadError ? (
                            <div className="mentality-overlay__chip is-error">
                                <AlertTriangle size={12} />
                                <span>{uploadError}</span>
                                <button
                                    type="button"
                                    className="mentality-overlay__chip-remove"
                                    onClick={onDismissError}
                                    aria-label="Dismiss error"
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        ) : (
                            <div className="mentality-overlay__chip">
                                <Paperclip size={12} />
                                <span className="mentality-overlay__chip-name">
                                    {docName || 'Pasted excerpt'}
                                </span>
                                {uploadTruncated && (
                                    <span className="mentality-overlay__chip-meta">truncated</span>
                                )}
                                <button
                                    type="button"
                                    className="mentality-overlay__chip-remove"
                                    onClick={onClearDoc}
                                    aria-label="Remove attachment"
                                >
                                    <X size={10} />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <textarea
                    className="mentality-overlay__textarea"
                    aria-label={`Ask ${GLOSSARY.MENTALITY.shortLabel}`}
                    placeholder={hasAnyContent
                        ? 'Ask about a situation you\'re in…'
                        : 'No curated rules yet'
                    }
                    value={query}
                    onChange={(e) => {
                        onQueryChange(e.target.value);
                        const el = e.target as HTMLTextAreaElement;
                        el.style.height = 'auto';
                        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (!sending) onSend();
                        }
                    }}
                    disabled={!hasAnyContent || sending}
                    rows={1}
                />

                <div className="mentality-overlay__actions">
                    <div className="mentality-overlay__actions-left">
                        <button
                            type="button"
                            className={`mentality-overlay__action${docName || docText ? ' is-active' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            aria-label="Attach a document"
                            title="Attach PDF, Word or text file"
                        >
                            <Paperclip size={16} />
                        </button>
                    </div>
                    <button
                        type="button"
                        className="mentality-overlay__send"
                        onClick={onSend}
                        disabled={!query.trim() || !hasAnyContent || sending}
                        aria-label="Ask"
                    >
                        {sending ? (
                            <RefreshCw size={14} className="mentality-overlay__send-spin" />
                        ) : (
                            <ArrowUp size={14} />
                        )}
                    </button>
                </div>
            </div>

            <p className="mentality-overlay__foot-note">
                {docText
                    ? 'Document excerpt attached. AI replies cite mentor-curated rules.'
                    : 'AI replies cite mentor-curated rules — no freelance advice.'}
            </p>
        </>
    );
}
